import React from "react";
import Select from "react-select";
import { ITourneyMgmtScheduleTemplateData } from "../../types/forms";
import {
    Controller,
    SubmitHandler,
    useFieldArray,
    useForm,
} from "react-hook-form";
import { Button, Col, Form, Row, Table } from "react-bootstrap";
import { capitalize, displayFormAlert } from "../../lib/utils";
import { useLoaderData, useLocation, useParams } from "react-router";
import { IField, ITournamentTemplate } from "../../types/api";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { useAppDispatch, useAppSelector } from "../../hooks/reduxHooks";
import { store } from "../..";
import {
    globalActions,
    selectAgeLevels,
    selectField,
} from "../../features/global/globalSlice";
import { getTemplate, updateTemplate } from "../../lib/api";

import "../../assets/css/wibb/pages/templates.scss";
import { alertActions } from "../../features/alert/alertSlice";
import { Link } from "react-router-dom";
import classNames from "classnames";

type TTournamentDays = "friday" | "saturday" | "sunday";
const tournamentDays: TTournamentDays[] = ["friday", "saturday", "sunday"];

interface FormElements extends HTMLFormControlsCollection {
    usernameInput: HTMLInputElement;
}

interface TemplateFormElement extends HTMLFormElement {
    readonly elements: FormElements;
}

export const loader = async ({ params }: { params: any }) => {
    const { fieldId } = params;
    let fields = store.getState().global.fields;
    let ageLevels = store.getState().global.ageLevels;

    const fieldPromise = fields.length
        ? Promise.resolve()
        : store.dispatch(globalActions.getFields());

    const ageLevelsPromise = ageLevels.length
        ? Promise.resolve()
        : store.dispatch(globalActions.getAgeLevels());

    const [{ template }] = await Promise.all([
        getTemplate("field", fieldId),
        fieldPromise,
        ageLevelsPromise,
    ]);

    fields = store.getState().global.fields;
    const field = selectField(store.getState(), parseInt(fieldId));

    return {
        header: `Edit ${field?.name} Template`,
        template: template,
    };
};

const Template = (): React.JSX.Element => {
    const { fieldId } = useParams();
    const dispatch = useAppDispatch();
    const { template } = useLoaderData() as {
        template: ITournamentTemplate;
    };
    const field = useAppSelector(state =>
        selectField(state, parseInt(fieldId as string)),
    ) as IField;
    const ageLevels = useAppSelector(selectAgeLevels);

    const [items, setItems] = React.useState({
        friday:
            template.template.schedule.friday &&
            Object.keys(template.template.schedule.friday).length !== 0
                ? template.template.schedule.friday.map((t, idx) => ({
                      id: idx,
                      time: t.time,
                      fields: t.fields?.map(f => ({
                          value: f,
                          label: field.diamonds.find(d => d.id === f)?.name,
                      })),
                  }))
                : [],
        saturday:
            template.template.schedule.saturday &&
            Object.keys(template.template.schedule.saturday).length !== 0
                ? template.template.schedule.saturday.map((t, idx) => ({
                      id: idx,
                      time: t.time,
                      fields:
                          t.fields?.map(f => ({
                              value: f,
                              label: field.diamonds.find(d => d.id === f)?.name,
                          })) || undefined,
                  }))
                : [],
        sunday:
            template.template.schedule.sunday &&
            Object.keys(template.template.schedule.sunday).length !== 0
                ? template.template.schedule.sunday.map((t, idx) => ({
                      id: idx,
                      time: t.time,
                      fields:
                          t.fields?.map(f => ({
                              value: f,
                              label: field.diamonds.find(d => d.id === f)?.name,
                          })) || undefined,
                  }))
                : [],
    });

    const [draggingIndex, setDraggingIndex] = React.useState<
        Record<TTournamentDays, number>
    >({
        friday: -1,
        saturday: -1,
        sunday: -1,
    });
    const [dragOverIndex, setDragOverIndex] = React.useState<
        Record<TTournamentDays, number>
    >({
        friday: -1,
        saturday: -1,
        sunday: -1,
    });

    const [placeholderDimensions, setPlaceholderDimensions] = React.useState({
        h: 0,
        w: 0,
    });

    const dragNode = React.useRef<EventTarget>();
    const defaultDiamondRestrictions: {
        [key: string]: { value: string; label: string }[] | null;
    } = {};

    for (const key in template.template.diamondRestrictions) {
        const valueArray = template.template.diamondRestrictions[key];

        if (Array.isArray(valueArray)) {
            defaultDiamondRestrictions[key] = valueArray.map(item => ({
                value: item,
                label: item,
            }));
        } else {
            // Preserve the null value or handle it as needed
            defaultDiamondRestrictions[key] = valueArray;
        }
    }

    const {
        register,
        formState: { errors },
        handleSubmit,
        getValues,
        setValue,
        watch,
        control,
    } = useForm({
        mode: "onChange",
        defaultValues: {
            friday: items["friday"],
            saturday: items["saturday"],
            sunday: items["sunday"],
            diamondRestrictions: defaultDiamondRestrictions,
        },
    });

    const {
        fields: fridayTimes,
        append: fridayAppend,
        remove: fridayRemove,
    } = useFieldArray({
        control,
        name: "friday",
    });

    const {
        fields: saturdayTimes,
        append: saturdayAppend,
        remove: saturdayRemove,
    } = useFieldArray({
        control,
        name: "saturday",
    });

    const {
        fields: sundayTimes,
        append: sundayAppend,
        remove: sundayRemove,
    } = useFieldArray({
        control,
        name: "sunday",
    });

    const watchFriday = watch("friday");
    const watchSaturday = watch("saturday");
    const watchSunday = watch("sunday");

    const onSubmit: SubmitHandler<
        ITourneyMgmtScheduleTemplateData
    > = async data => {
        await updateTemplate(template.id, data)
            .then(response => {
                dispatch(
                    alertActions.success({
                        title: "Template updated successfully",
                        dismissible: true,
                    }),
                );
            })
            .catch(e => {
                dispatch(
                    alertActions.error({
                        title: "The server encountered a problem updating the template.",
                        text: "If the problem continues, please reach out to an admin.",
                        dismissible: true,
                    }),
                );
            });
    };

    // const handleDragStart = (
    //     e: React.DragEvent<HTMLDivElement>,
    //     index: number,
    // ) => {
    //     const { currentTarget } = e;
    //     setDraggingIndex(index);

    //     dragNode.current = currentTarget;
    //     e.dataTransfer.effectAllowed = "move";
    //     e.dataTransfer.setData("text/html", currentTarget.id);
    //     const { clientWidth, clientHeight } = currentTarget;
    //     setPlaceholderDimensions({ w: clientWidth, h: clientHeight });
    // };
    const handleDragStart = (
        e: React.DragEvent<HTMLDivElement>,
        day: TTournamentDays,
        index: number,
    ) => {
        const { target } = e;
        setDraggingIndex(prev => {
            return {
                ...prev,
                [day]: index,
            };
        });

        dragNode.current = target;
        e.dataTransfer.effectAllowed = "move";
        e.dataTransfer.setData("text/html", e.currentTarget.id);
        const { clientWidth, clientHeight } = e.currentTarget;
        setPlaceholderDimensions({ w: clientWidth, h: clientHeight });
    };

    // const handleDragEnter = (
    //     e: React.DragEvent<HTMLDivElement>,
    //     index: number,
    // ) => {
    //     e.preventDefault();
    //     if (dragNode.current !== e.target) {
    //         setDragOverIndex(index);
    //         let newItems = [...items];
    //         newItems.splice(
    //             dragOverIndex === -1 ? items.length : dragOverIndex,
    //             0,
    //             newItems.splice(draggingIndex, 1)[0],
    //         );
    //         setDraggingIndex(
    //             dragOverIndex === -1 ? items.length - 1 : dragOverIndex,
    //         );
    //         setItems(newItems);
    //     }
    // };

    const handleDragEnter = (
        e: React.DragEvent<HTMLDivElement>,
        day: TTournamentDays,
        index: number,
    ) => {
        e.preventDefault();
        if (dragNode.current !== e.currentTarget) {
            setDragOverIndex(prev => ({ ...prev, [day]: index }));
            let newItems = [...items[day]];
            newItems.splice(
                dragOverIndex[day] === -1
                    ? items[day].length
                    : dragOverIndex[day],
                0,
                newItems.splice(draggingIndex[day], 1)[0],
            );

            setDraggingIndex(prev => ({
                ...prev,
                [day]:
                    dragOverIndex[day] === -1
                        ? items[day].length - 1
                        : dragOverIndex[day],
            }));

            setItems(prev => ({ ...prev, [day]: newItems }));
        }
    };

    // const handleDragEnd = () => {
    //     setDraggingIndex(-1);
    //     setDragOverIndex(-1);
    // };
    const handleDragEnd = (day: string) => {
        setDraggingIndex(prev => {
            return {
                ...prev,
                [day]: -1,
            };
        });
        setDragOverIndex(prev => {
            return {
                ...prev,
                [day]: -1,
            };
        });
    };

    return (
        <section className="templateContent" data-testid="templateContent">
            <Form onSubmit={handleSubmit(onSubmit)}>
                <Row className="mb-3">
                    <Col>
                        <h3>Game Times</h3>
                        <p>
                            <span className="h5">Notes:</span>
                            <br />
                            Games will be created in the order the times are
                            defined for each day from top to bottom
                            <br />
                            If a time slot can only be played on certain fields
                            (lights, watering schedules, etc), include the
                            fields in the drop-down below the time slot. An
                            empty box will mean all fields can sustain that time
                            slot.
                        </p>
                    </Col>
                </Row>
                <Row>
                    <Col xs={12} lg={4}>
                        <h4>Friday Times</h4>
                        {fridayTimes.map((t, index) => (
                            <div
                                // draggable={true}
                                key={`friday-${t.id}`}
                                className="templateTime"
                                // onDragStart={e =>
                                //     handleDragStart(e, "saturday", index)
                                // }
                                // onDragEnter={e =>
                                //     handleDragEnter(e, "saturday", index)
                                // }
                                // onDragEnd={() => handleDragEnd("saturday")}
                            >
                                {t.id === dragOverIndex["friday"] ? (
                                    <div
                                        className="dragPlaceholder"
                                        style={{
                                            width: `${placeholderDimensions.w}px`,
                                            height: `${placeholderDimensions.h}px`,
                                        }}
                                    ></div>
                                ) : (
                                    // <div>{t.time}</div>
                                    <>
                                        {/* <span className="handle"></span> */}
                                        <Form.Control
                                            type="text"
                                            {...register(
                                                `friday.${index}.time` as const,
                                            )}
                                            placeholder={`Friday time`}
                                            defaultValue={t.time}
                                            aria-invalid={
                                                errors["friday"] &&
                                                errors["friday"][index]
                                                    ? "true"
                                                    : "false"
                                            }
                                        />
                                        <Button
                                            type="button"
                                            className="no-margin-btn"
                                            onClick={() => fridayRemove(index)}
                                        >
                                            Delete Time
                                        </Button>
                                        <div
                                            className={classNames(
                                                "fieldTimeRestrictions",
                                            )}
                                        >
                                            <Controller
                                                name={`friday.${index}.fields`}
                                                control={control}
                                                defaultValue={t.fields}
                                                render={({
                                                    field: selectField,
                                                }) => (
                                                    <Select
                                                        {...selectField}
                                                        isMulti
                                                        options={field.diamonds.map(
                                                            d => {
                                                                return {
                                                                    value: d.id,
                                                                    label: d.name,
                                                                };
                                                            },
                                                        )}
                                                        defaultValue={t.fields}
                                                    />
                                                )}
                                            />
                                        </div>
                                    </>
                                )}
                            </div>
                        ))}
                        <Button
                            type="button"
                            onClick={() =>
                                fridayAppend({
                                    id: watchFriday.length,
                                    time: "",
                                    fields: [],
                                })
                            }
                        >
                            Add New Friday Time
                        </Button>
                    </Col>
                    <Col xs={12} lg={4}>
                        <h4>Saturday Times</h4>
                        {saturdayTimes.map((t, index) => (
                            <div
                                // draggable={true}
                                key={`saturday-${t.id}`}
                                className="templateTime"
                                // onDragStart={e =>
                                //     handleDragStart(e, "saturday", index)
                                // }
                                // onDragEnter={e =>
                                //     handleDragEnter(e, "saturday", index)
                                // }
                                // onDragEnd={() => handleDragEnd("saturday")}
                            >
                                {t.id === dragOverIndex["saturday"] ? (
                                    <div
                                        className="dragPlaceholder"
                                        style={{
                                            width: `${placeholderDimensions.w}px`,
                                            height: `${placeholderDimensions.h}px`,
                                        }}
                                    ></div>
                                ) : (
                                    // <div>{t.time}</div>
                                    <>
                                        {/* <span className="handle"></span> */}
                                        <Form.Control
                                            type="text"
                                            {...register(
                                                `saturday.${index}.time` as const,
                                            )}
                                            placeholder={`Saturday time`}
                                            defaultValue={t.time}
                                            aria-invalid={
                                                errors["saturday"] &&
                                                errors["saturday"][index]
                                                    ? "true"
                                                    : "false"
                                            }
                                        />
                                        <Button
                                            type="button"
                                            className="no-margin-btn"
                                            onClick={() =>
                                                saturdayRemove(index)
                                            }
                                        >
                                            Delete Time
                                        </Button>
                                        <div
                                            className={classNames(
                                                "fieldTimeRestrictions",
                                            )}
                                        >
                                            <Controller
                                                name={`saturday.${index}.fields`}
                                                control={control}
                                                defaultValue={t.fields}
                                                render={({
                                                    field: selectField,
                                                }) => (
                                                    <Select
                                                        {...selectField}
                                                        isMulti
                                                        options={field.diamonds.map(
                                                            d => {
                                                                return {
                                                                    value: d.id,
                                                                    label: d.name,
                                                                };
                                                            },
                                                        )}
                                                        defaultValue={t.fields}
                                                    />
                                                )}
                                            />
                                        </div>
                                    </>
                                )}
                            </div>
                        ))}
                        <Button
                            type="button"
                            onClick={() =>
                                saturdayAppend({
                                    id: watchSaturday.length,
                                    time: "",
                                    fields: [],
                                })
                            }
                        >
                            Add New Saturday Time
                        </Button>
                    </Col>
                    <Col xs={12} lg={4}>
                        <h4>Sunday Times</h4>
                        {sundayTimes.map((t, index) => (
                            <div
                                // draggable={true}
                                key={`sunday-${t.id}`}
                                className="templateTime"
                                // onDragStart={e =>
                                //     handleDragStart(e, "saturday", index)
                                // }
                                // onDragEnter={e =>
                                //     handleDragEnter(e, "saturday", index)
                                // }
                                // onDragEnd={() => handleDragEnd("saturday")}
                            >
                                {t.id === dragOverIndex["sunday"] ? (
                                    <div
                                        className="dragPlaceholder"
                                        style={{
                                            width: `${placeholderDimensions.w}px`,
                                            height: `${placeholderDimensions.h}px`,
                                        }}
                                    ></div>
                                ) : (
                                    // <div>{t.time}</div>
                                    <>
                                        {/* <span className="handle"></span> */}
                                        <Form.Control
                                            type="text"
                                            {...register(
                                                `sunday.${index}.time` as const,
                                            )}
                                            placeholder={`Sunday time`}
                                            defaultValue={t.time}
                                            aria-invalid={
                                                errors["sunday"] &&
                                                errors["sunday"][index]
                                                    ? "true"
                                                    : "false"
                                            }
                                        />
                                        <Button
                                            type="button"
                                            className="no-margin-btn"
                                            onClick={() => sundayRemove(index)}
                                        >
                                            Delete Time
                                        </Button>
                                        <div
                                            className={classNames(
                                                "fieldTimeRestrictions",
                                            )}
                                        >
                                            <Controller
                                                name={`sunday.${index}.fields`}
                                                control={control}
                                                defaultValue={t.fields}
                                                render={({
                                                    field: selectField,
                                                }) => (
                                                    <Select
                                                        {...selectField}
                                                        isMulti
                                                        options={field.diamonds.map(
                                                            d => {
                                                                return {
                                                                    value: d.id,
                                                                    label: d.name,
                                                                };
                                                            },
                                                        )}
                                                        defaultValue={t.fields}
                                                    />
                                                )}
                                            />
                                        </div>
                                    </>
                                )}
                            </div>
                        ))}
                        <Button
                            type="button"
                            onClick={() =>
                                sundayAppend({
                                    id: watchSunday.length,
                                    time: "",
                                    fields: [],
                                })
                            }
                        >
                            Add New Sunday Time
                        </Button>
                    </Col>
                </Row>
                <Row className="mt-5 mb-3">
                    <Col>
                        <h3>Field Restrictions</h3>
                        <p>
                            <span className="h5">Note:</span>
                            For each field at this location, select all age
                            levels that the field can support. This is used for
                            assigning age levels to fields during pool play
                            schedule generation.{" "}
                            <b>
                                If a field can support all age levels, leave the
                                value blank
                            </b>
                        </p>
                    </Col>
                </Row>
                <Row>
                    <Col>
                        {field.diamonds ? (
                            field.diamonds.map(diamond => (
                                <Row
                                    key={`restriction-${diamond.id}`}
                                    className="fieldRestriction"
                                >
                                    <Col xs={2}>
                                        <h5>{diamond?.name || diamond.id}</h5>
                                    </Col>
                                    <Col xs={10}>
                                        <Controller
                                            name={`diamondRestrictions.${diamond.id}`}
                                            control={control}
                                            defaultValue={
                                                defaultDiamondRestrictions[
                                                    diamond.id
                                                ]
                                            }
                                            render={({ field }) => (
                                                <Select
                                                    {...field}
                                                    isMulti
                                                    options={ageLevels.map(
                                                        a => {
                                                            return {
                                                                value: a.ageLevel,
                                                                label: a.ageLevel,
                                                            };
                                                        },
                                                    )}
                                                    defaultValue={
                                                        defaultDiamondRestrictions[
                                                            diamond.id
                                                        ]
                                                    }
                                                />
                                            )}
                                        />
                                    </Col>
                                </Row>
                            ))
                        ) : (
                            <h6>
                                There are currently no diamonds assigned to this
                                field. In order to add age level restrictions to
                                diamonds, first{" "}
                                <Link
                                    to={`/tournament-management/fields/edit/${fieldId}`}
                                >
                                    add the diamonds to the field
                                </Link>{" "}
                                then come back here and you can add the age
                                level restrictions to each diamond.
                            </h6>
                        )}
                    </Col>
                </Row>
                <Row>
                    <Col>
                        <Button type="submit">Save Template</Button>
                    </Col>
                </Row>
            </Form>
        </section>
    );
};

export default Template;
